import { Box, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import { TSalesProcessingRow } from 'redux/reducers/salesProcessing/types';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { DATES_ARRAY, DATES_ID } from 'services/constants/dates-constants';
import { ESortDirection, ETableStructureType } from 'ui/EasTable/types';
import { TFilters } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import {
	getProcessingTypesRequest,
	getUploadedFilesRequest,
	downloadFileRequest,
	getFileStatusesRequest,
	getEmInvoicingFeeTypesRequest,
} from 'redux/reducers/salesProcessing/reducer';
import { getUsersListRequest } from 'redux/reducers/user/reducer';
import {
	getSalesProcessingUploadedFiles,
	getSalesProcessingLoading,
	getSalesProcessingProcessingTypesLoading,
	getSalesProcessingProcessingTypes,
	getSalesProcessingFileStatuses,
	getSalesProcessingFileStatusesLoading,
} from 'redux/reducers/salesProcessing/selectors';
import { getUsersList } from 'redux/reducers/user/selectors';
import { tableStructureFunction } from './tableStructure';
import { SalesReportProcessingDialog } from './SalesReportProcessingDialog';
import { ENavigateNames } from '../../routes/constants';

export const SalesProcessing: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const salesProcessingList = useSelector(getSalesProcessingUploadedFiles);
	const salesProcessingLoading = useSelector(getSalesProcessingLoading);

	const usersList = useSelector(getUsersList);

	const processingTypesLoading = useSelector(getSalesProcessingProcessingTypesLoading);
	const processingTypes = useSelector(getSalesProcessingProcessingTypes);

	const fileStatusesLoading = useSelector(getSalesProcessingFileStatusesLoading);
	const fileStatuses = useSelector(getSalesProcessingFileStatuses);
	const tableStructure = tableStructureFunction({
		processingTypes: processingTypes ?? [],
		fileStatuses: fileStatuses ?? [],
		usersList: usersList ?? [],
	});

	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (item.type === ETableStructureType.DATE && navigateParams[item.selector]) {
			if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
				if (navigateParams[`${item.selector}StartDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${item.selector}StartDate`] as string,
						operator: '>=',
					});
				}
				if (navigateParams[`${item.selector}EndDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${item.selector}EndDate`] as string,
						operator: '<=',
					});
				}
				return acc;
			}
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}

		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		dispatch(
			getUploadedFilesRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy: [
					{
						fieldName: navigateParams?.sortIndex
							? tableStructure[+navigateParams.sortIndex].selector
							: 'id',
						direction: navigateParams?.sortDirection
							? navigateParams.sortDirection === ESortDirection.DESC
								? ESortDirection.DESC
								: ESortDirection.ASC
							: ESortDirection.DESC,
					},
				],
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getProcessingTypesRequest());
		dispatch(getFileStatusesRequest());
		dispatch(getUsersListRequest());
		dispatch(getEmInvoicingFeeTypesRequest());
	}, [dispatch]);

	const [openUploadSalesReport, setOpenUploadSalesReport] = useState(false);
	const [openProcessSalesReport, setOpenProcessSalesReport] = useState(false);
	const handleCloseUploadSalesReport = () => {
		setOpenUploadSalesReport(false);
	};

	const handleCloseProcessSalesReport = () => {
		setOpenProcessSalesReport(false);
	};

	const handleDownloadSalesReport = (fileId: number, fileName: string) => {
		dispatch(downloadFileRequest({ fileId, fileName }));
	};

	const [selectedRow, setSelectedRow] = useState<TSalesProcessingRow | null>(null);
	const handleAction = (id: number, typeOfDialog: string) => {
		setSelectedRow(salesProcessingList?.data?.find((item) => item.id === id) ?? null);
		if (typeOfDialog === 'Download File') {
			handleDownloadSalesReport(
				id,
				salesProcessingList?.data?.find((item) => item.id === id)?.fileName ?? 'unknown',
			);
		}
		if (typeOfDialog === 'Process File') {
			setOpenProcessSalesReport(true);
		}
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TSalesProcessingRow>
				tableStructure={tableStructure}
				items={salesProcessingList}
				itemsIsLoading={salesProcessingLoading}
				handleAction={handleAction}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						{ENavigateNames.SALES_PROCESSING}
					</Typography>
				</Box>
			</EasTableWithFilter>
			<SalesReportProcessingDialog
				open={openProcessSalesReport}
				onClose={handleCloseProcessSalesReport}
				data={selectedRow}
				reloadBody={{
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
					filters: navigateParamsWithoutPage,
					orderBy: [
						{
							fieldName: navigateParams?.sortIndex
								? tableStructure[+navigateParams.sortIndex].selector
								: 'id',
							direction: navigateParams?.sortDirection
								? navigateParams.sortDirection === ESortDirection.DESC
									? ESortDirection.DESC
									: ESortDirection.ASC
								: ESortDirection.DESC,
						},
					],
				}}
			/>
		</Box>
	);
};
